/** Agency fields used to seed section placeholders in the site builder. */
export interface AgencyBrandProfile {
  name?: string;
  phone?: string;
  email?: string;
  whatsapp?: string;
  address?: string;
  hours?: string;
  logoUrl?: string;
}

const BRAND_KEYS = [
  'logoText',
  'phone',
  'email',
  'whatsapp',
  'address',
  'hours',
  'logoUrl',
  'legal',
] as const;

const DEMO_PLACEHOLDERS = new Set([
  'drivex',
  'contact@drivex.ma',
  '+212528840000',
  '+212 528 84 00 00',
  '+212 528 840 000',
  '+212 600 000 000',
  'av. hassan ii, agadir 80000, morocco',
  'agadir, morocco',
]);

function nonEmpty(value: unknown): value is string {
  if (typeof value !== 'string' || value.trim() === '') return false;
  const normalized = value.trim().toLowerCase();
  if (DEMO_PLACEHOLDERS.has(normalized)) return false;
  if (normalized.startsWith('© 2026 drivex') || normalized.includes('drivex.ma')) return false;
  return true;
}

/** Fill empty brand fields on section content from the live agency profile. */
export function applyAgencyBrandToContent(
  content: Record<string, unknown>,
  agency?: AgencyBrandProfile | null,
): Record<string, unknown> {
  if (!agency) return content;
  const next: Record<string, unknown> = { ...content };
  const name = nonEmpty(agency.name) ? agency.name.trim() : '';

  if (!nonEmpty(next.logoText) && name) next.logoText = name;
  if (!nonEmpty(next.phone) && nonEmpty(agency.phone)) next.phone = agency.phone.trim();
  if (!nonEmpty(next.email) && nonEmpty(agency.email)) next.email = agency.email.trim();
  if (!nonEmpty(next.whatsapp) && nonEmpty(agency.whatsapp ?? agency.phone)) {
    next.whatsapp = (agency.whatsapp ?? agency.phone)!.trim();
  }
  if (!nonEmpty(next.address) && nonEmpty(agency.address)) next.address = agency.address.trim();
  if (!nonEmpty(next.hours) && nonEmpty(agency.hours)) next.hours = agency.hours.trim();
  if (!nonEmpty(next.logoUrl) && nonEmpty(agency.logoUrl)) next.logoUrl = agency.logoUrl.trim();
  if (!nonEmpty(next.legal) && name) {
    next.legal = `© ${new Date().getFullYear()} ${name}. All rights reserved.`;
  }

  return next;
}

export function agencyBrandHasValues(agency?: AgencyBrandProfile | null): boolean {
  if (!agency) return false;
  return BRAND_KEYS.some((key) => {
    if (key === 'logoText' || key === 'legal') return nonEmpty(agency.name);
    if (key === 'whatsapp') return nonEmpty(agency.whatsapp ?? agency.phone);
    return nonEmpty(agency[key as keyof AgencyBrandProfile]);
  });
}
